interface CurrentReportContext {
  calendarEventId: string
  date: string
  projectName?: string
  projectId?: string
  lastVisitedPage?: string
}

const currentReport = ref<CurrentReportContext | null>(null)
const currentReportKey = 'currentReport'

if (process.client && currentReport.value === null) {
  const stored = localStorage.getItem(currentReportKey)
  currentReport.value = stored ? JSON.parse(stored) : null

  window.addEventListener('storage', (e) => {
    if (e.key === currentReportKey) {
      currentReport.value = e.newValue ? JSON.parse(e.newValue) : null
    }
  })
}

export const useCurrentReport = () => {
  const setCurrentReport = (context: CurrentReportContext) => {
    if (process.client) {
      if (!context.lastVisitedPage) {
        const currentPath = window.location.pathname + window.location.search
        if (!currentPath.includes('project-data')) {
          context.lastVisitedPage = currentPath
        }
      }
      localStorage.setItem(currentReportKey, JSON.stringify(context))
      currentReport.value = {
        ...context,
        lastVisitedPage: currentReport.value?.lastVisitedPage
      }
    }
  }

  const getCurrentReport = (): CurrentReportContext | null => {
    return currentReport.value
  }

  const clearCurrentReport = () => {
    if (process.client) {
      localStorage.removeItem(currentReportKey)
      currentReport.value = null
    }
  }

  const hasCurrentReport = (): boolean => {
    return currentReport.value !== null
  }

  const setLastVisitedPage = (pagePath: string) => {
    if (process.client && currentReport.value) {
      const updated = { ...currentReport.value, lastVisitedPage: pagePath }
      localStorage.setItem(currentReportKey, JSON.stringify(updated))
      currentReport.value = updated
    }
  }

  const navigateToCurrentReport = () => {
    const current = getCurrentReport()
    if (current) {
      navigateTo({
        path: `/project-data/${current.calendarEventId}`,
        query: { date: current.date }
      })
    }
  }

  const navigateToLastVisitedPage = () => {
    const current = getCurrentReport()

    if (current && current.lastVisitedPage) {
      navigateTo(current.lastVisitedPage)
    } else {
      navigateToCurrentReport()
    }
  }

  const calculateReportProgress = async (): Promise<{
    percentage: number
    completedSections: number
    totalSections: number
  }> => {
    const current = getCurrentReport()
    if (!current) {
      return { percentage: 0, completedSections: 0, totalSections: 6 }
    }

    try {
      const { fetchCalendarEvent } = useCalendarEvent()
      const calendarEvent = await fetchCalendarEvent(current.calendarEventId)

      if (!calendarEvent) {
        return { percentage: 0, completedSections: 0, totalSections: 6 }
      }

      let completedSections = 0
      const totalSections = 6

      const project = calendarEvent.project
      if (project?.street && project?.zip && project?.city) {
        completedSections++
      }

      const causesOfDamage = calendarEvent.causesOfDamage
      if (causesOfDamage && causesOfDamage.length > 0) {
        completedSections++
      }

      const measurementProtocol = calendarEvent.measurementProtocol
      if (measurementProtocol) {
        const methods = measurementProtocol.measurementMethods
        if (Object.values(methods).some((method: any) => method.isSelected)) {
          completedSections++
        }
      }

      const damageMitigation = calendarEvent.damageMitigation
      if (damageMitigation && damageMitigation.trim().length > 0) {
        completedSections++
      }

      const damageReport = calendarEvent.damageReport
      if (damageReport && (damageReport.damageReport || damageReport.damageDescription)) {
        completedSections++
      }

      const presentPersons = calendarEvent.presentPersons
      if (presentPersons) {
        const hasPersons = Object.values(presentPersons).some(
          (persons: any) =>
            Array.isArray(persons) &&
            persons.some((person: any) => person.firstName && person.firstName.trim().length > 0)
        )
        if (hasPersons) {
          completedSections++
        }
      }

      const percentage = Math.round((completedSections / totalSections) * 100)

      return { percentage, completedSections, totalSections }
    } catch (error) {
      return { percentage: 0, completedSections: 0, totalSections: 6 }
    }
  }

  return {
    setCurrentReport,
    getCurrentReport,
    clearCurrentReport,
    hasCurrentReport,
    setLastVisitedPage,
    navigateToCurrentReport,
    navigateToLastVisitedPage,
    calculateReportProgress,
    currentReport: readonly(currentReport)
  }
}
